import { createContext, useContext, useState, useEffect } from "react";

// Local imports
import { useAuth } from './AuthContext';
import { api } from '../api';
import { ACTIVITY_TYPES } from "../api/constants";

const ActivitiesCompletionContext = createContext(null);

const SURVEY_REQUIRED_VIDEO_ID = process.env.REACT_APP_SURVEY_REQUIRED_VIDEO_ID ? Number(process.env.REACT_APP_SURVEY_REQUIRED_VIDEO_ID) : 2;

export const ActivitiesCompletionProvider = ({children}) => {
    const [ assignedVideos, setAssignedVideos ] = useState([]);
    const [ watchedAllVideos, setWatchedAllVideos ] = useState(false);
    const [ hasSurvey, setHasSurvey ] = useState(false);
    const [ submittedSurvey, setSubmittedSurvey ] = useState(false);
    const [ activitiesCompleted, setActivitiesCompleted ] = useState(false);
    // Counter that increments on each reload request. A boolean would stick at
    // `true` after the first call, so a second activity completion in the same
    // session wouldn't re-run the fetch effects below (their deps wouldn't change).
    const [ reloadActivities, setReloadCounter ] = useState(0);
    const setReloadActivities = () => setReloadCounter(c => c + 1);
    const [ modalHasBeenShown, setModalHasBeenShown ] = useState(null);
    const [ openModal, setOpenModal ] = useState(false);
    const { isLoggedIn, isAdmin } = useAuth();

    useEffect(() => {
        if (!isLoggedIn || isAdmin) return;

        // Fetch assigned videos
        const fetchVideos = async () => {
            try {
                const data = await api.get('/videos');
                setAssignedVideos(data);
            } catch (error) {
                console.error("Error fetching videos:", error);
            }
        }
        fetchVideos();
    }, [isLoggedIn, reloadActivities])

    useEffect(() => {
        if (assignedVideos.length === 0) return;

        // Check if all videos have been watched
        for (const video of assignedVideos) {
            if (!video.completed) {
                setWatchedAllVideos(false);
                return;
            }
            // Check if survey is assigned
            if (video.id === SURVEY_REQUIRED_VIDEO_ID) {
                setHasSurvey(true);
            }
        }
        setWatchedAllVideos(true);
    }, [assignedVideos]);
    
    useEffect(() => {
        if (!isLoggedIn || !hasSurvey) return;

        // Fetch survey submission status
        const fetchSurveyStatus = async () => {
            try {
                const data = await api.get(`/activity/has-performed?activity=${ACTIVITY_TYPES.USER_SUBMITTED_SURVEY}`);
                setSubmittedSurvey(data.hasPerformed);
            } catch (error) {
                console.error("Error fetching survey submission status:", error);
            }
        }
        fetchSurveyStatus();
    }, [isLoggedIn, hasSurvey, reloadActivities]);

    useEffect(() => {
        if (!isLoggedIn) return;

        // Check if all required activities are completed
        if (hasSurvey) {
            setActivitiesCompleted(watchedAllVideos && submittedSurvey);
        } else {
            setActivitiesCompleted(watchedAllVideos);
        }
    }, [isLoggedIn, hasSurvey, watchedAllVideos, submittedSurvey]);

    useEffect(() => {
        if (!isLoggedIn) return;

        // Fetch modal-shown status
        const fetchModalStatus = async () => {
            try {
                const data = await api.get('/participant/modal-shown');
                setModalHasBeenShown(data.modalHasBeenShown);
            } catch (error) {
                console.error("Error fetching modal shown status:", error);
            }
        }
        fetchModalStatus();
    }, [isLoggedIn]);

    // Single source of truth for opening the completion modal. Only fires in
    // response to a completion event in this session (reloadActivities > 0),
    // never on a plain page load — otherwise a user with activitiesCompleted
    // already true on the server would see the modal pop on every navigation.
    useEffect(() => {
        if (reloadActivities === 0) return;
        if (activitiesCompleted && modalHasBeenShown === false) {
            setOpenModal(true);
            setModalHasBeenShown(true);
            api.post('/participant/modal-shown').catch(err => {
                console.error('Error updating modal shown status:', err);
            });
        }
    }, [reloadActivities, activitiesCompleted, modalHasBeenShown]);

    return (
        <ActivitiesCompletionContext.Provider value={{activitiesCompleted, setReloadActivities, modalHasBeenShown, openModal, setOpenModal, watchedAllVideos}}>
            {children}
        </ActivitiesCompletionContext.Provider>
    )
}

export const useActivitiesCompletion = () => {
    const context = useContext(ActivitiesCompletionContext);
    if (!context) {
        throw new Error('useActivitiesCompletion must be used within an ActivitiesCompletionProvider');
    }
    return context;
};